import { castDraft } from 'immer'
import type { StateCreator } from 'zustand'

import type { BufferEditor } from '~publish/legacy/editor/BufferEditor/types.plate'

import type { AppSlice } from '../appSlice'
import type { CapabilitySlice } from '../capabilitySlice'
import type { ChannelSlice } from '../channelSlice'
import type { DirtySlice } from '../dirtySlice'
import type { ModeSlice } from '../modeSlice/modeSlice'
import type { PostSlice } from '../postSlice/postSlice'
import type { SaveErrorsSlice } from '../saveErrorsSlice'

/**
 * Opaque, non-draftable container for a live `BufferEditor`. Plate/Slate
 * editors are plain objects (their own prototype IS `Object.prototype`), so
 * Immer's producer treats them as ordinary state to draft and auto-freeze —
 * exactly what an identity mirror must not do (freezing would break Slate's
 * in-place mutation model; drafting would silently fork the reference).
 * Wrapping in a class instance gives it a different prototype, which is
 * enough for Immer to treat the whole thing as an opaque leaf: copied by
 * reference, never drafted, never frozen. (Immer's documented `[immerable]`
 * marker does NOT help here — it only *allows* drafting a class instance
 * that would otherwise be skipped; it can't *prevent* drafting of something
 * that already passes Immer's plain-object check, which a Slate editor
 * does.) Verified by `editorSlice.test.ts`'s freeze assertion.
 */
class EditorRef {
  constructor(readonly editor: BufferEditor) {}
}

export interface EditorState {
  /**
   * Live Plate/Slate editor instances, keyed by legacy `Draft.id` — which is
   * the SERVICE NAME (`service.name`, e.g. 'twitter'), or 'omni', NOT a
   * channel/profile id. This is a different id space than `channelSlice`
   * (profile-id-keyed) — the same distinction CORE-7311 got wrong once
   * already for `activeChannel`/`expandedComposerId`. A Phase 2 reader must
   * key this record by service name, or translate explicitly; don't assume
   * profile-id keying just because the rest of this store uses it.
   *
   * Mirrors `ComposerStore`'s `draft.editorState` by identity, not value —
   * see `~/notes/notes/claude/plans/composer/editor-state-migration.md`: two
   * independently-mutating editor instances for the same draft would diverge
   * the instant a keystroke lands in whichever one Plate actually mounts, so
   * this holds a pointer to the SAME instance Flux owns, never a clone.
   */
  editors: Record<string, EditorRef>
  /**
   * True once `installFluxToEditorStateMirror` has run its first synchronous
   * pass. Same role as `omniBridgeReady`/`channelBridgeReady`: a reader must
   * never see a half-seeded collection. No read side exists yet (CORE-7765),
   * but the gate ships now so that phase only has to consume it.
   */
  editorBridgeReady: boolean
}

export interface EditorActions {
  /** Written only by `installFluxToEditorStateMirror` — see CORE-7187 Phase 1. */
  setEditor: (draftId: string, editor: BufferEditor) => void
  removeEditor: (draftId: string) => void
  setEditorBridgeReady: (ready: boolean) => void
}

export type EditorSlice = EditorState & EditorActions

/** Unwraps the stored `EditorRef`, if any, back to the plain `BufferEditor`. */
export function selectEditorForDraft(
  state: Pick<EditorState, 'editors'>,
  draftId: string,
): BufferEditor | undefined {
  return state.editors[draftId]?.editor
}

export const createEditorSlice: StateCreator<
  AppSlice &
    CapabilitySlice &
    ChannelSlice &
    DirtySlice &
    ModeSlice &
    PostSlice &
    SaveErrorsSlice &
    EditorSlice,
  [['zustand/devtools', never], ['zustand/immer', never]],
  [],
  EditorSlice
> = (set) => ({
  editors: {},
  editorBridgeReady: false,

  setEditor: (draftId, editor): void =>
    set(
      (state) => {
        // castDraft: EditorRef is deliberately opaque to Immer (see above),
        // so it can never structurally satisfy WritableDraft<EditorRef> —
        // this is Immer's documented escape hatch for exactly that case.
        state.editors[draftId] = castDraft(new EditorRef(editor))
      },
      undefined,
      'editorSlice:setEditor',
    ),

  removeEditor: (draftId): void =>
    set(
      (state) => {
        delete state.editors[draftId]
      },
      undefined,
      'editorSlice:removeEditor',
    ),

  setEditorBridgeReady: (ready): void =>
    set(
      (state) => {
        state.editorBridgeReady = ready
      },
      undefined,
      'editorSlice:setEditorBridgeReady',
    ),
})
